import { useCallback, type MutableRefObject } from "react";
import { useForm as useFormHook } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { timezoneSchema, type TimezoneFormData } from "@core/features/profile";
import { updateProfileSettings } from "@core/features/profile/utils/updateProfileSettings";
import { getTimezoneOptions, getUserTimezone } from "@core/features/timezones";
import {
  getAction,
  isActionCompleted,
  ACTION_TYPE_SLUG,
  ACTION_STATUS,
  type ActionListResponse,
} from "@core/features/actions";
import { apiRequest } from "@core/services/api/apiRequest";
import { usePlatformStore } from "@platform/store";
import {
  useStepFormSync,
  type StepValidationState,
} from "@platform/features/onboarding";
import { User } from "@platform/types/platform.types";
import { Loader } from "@themes/pollinator/ui/Loader";
import { TimezoneForm } from "./TimezoneForm";

export type Props = {
  /** Optional callback when timezone is saved successfully (used in onboarding) */
  onSaveSuccess?: () => void;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Whether to show the cancel button (defaults to true when form is dirty) */
  showCancelButton?: boolean;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** Size variant for the timezone select */
  size?: "default" | "hero";
  /** Label for the timezone field */
  label?: string;
  /** Layout variant for the form field */
  layout?: "vertical" | "horizontal";
  /** Additional CSS classes for the container */
  className?: string;
  /** When true, detect the browser timezone if the user has no saved value */
  detectTimezone?: boolean;
};

/**
 * Container component for TimezoneForm.
 * Handles mutations, store access, and form state management.
 *
 * When used standalone (settings page), auto-completes any pending update_timezone action.
 * When onSaveSuccess is provided (onboarding), delegates status update to parent.
 */
export function TimezoneFormContainer({
  onSaveSuccess,
  primaryButtonText = "Save",
  showCancelButton,
  showActionButtons = true,
  onStepStateChange,
  submitRef,
  size,
  label,
  layout,
  className,
  detectTimezone = false,
}: Props = {}) {
  const user = usePlatformStore((s) => s.user);
  const setUser = usePlatformStore((s) => s.setUser);
  const timezoneOptions = getTimezoneOptions();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: TimezoneFormData) => {
      // Timezone is stored as an array with one element in the form
      const timezone = data.timezone?.[0];
      return updateProfileSettings({
        timezone,
        role: user?.role || undefined,
      });
    },
    onSuccess: async (data) => {
      setUser(data.user_data as unknown as User);

      // If onSaveSuccess is provided (e.g., onboarding), delegate to parent
      if (onSaveSuccess) {
        onSaveSuccess();
        return;
      }

      // Auto-complete any pending update_timezone action (standalone usage)
      // Fetch fresh actions to ensure we have the latest data
      try {
        const actionsData = (await apiRequest(
          "/actions/",
          "GET"
        )) as ActionListResponse;
        const actions = actionsData?.actions ?? [];
        const action = getAction(actions, ACTION_TYPE_SLUG.UPDATE_TIMEZONE);

        if (action && !isActionCompleted(action)) {
          await apiRequest(`/actions/${action.id}/status/`, "POST", {
            status: ACTION_STATUS.COMPLETED,
          });
          await queryClient.invalidateQueries({ queryKey: ["actions"] });
        }
      } catch {
        // Silently fail - timezone was saved successfully, action completion is secondary
      }
    },
  });

  // When detectTimezone is enabled and the user still has the backend default ('UTC'),
  // use browser detection to pre-fill. If they've explicitly set a non-default timezone,
  // respect their choice even if detectTimezone is true.
  const shouldDetect = detectTimezone && (!user?.timezone || user.timezone === "UTC");
  const detectedTimezone = shouldDetect
    ? getUserTimezone(timezoneOptions)?.value
    : undefined;

  const form = useFormHook<TimezoneFormData>({
    resolver: zodResolver(timezoneSchema),
    mode: "all",
    defaultValues: {
      timezone: detectedTimezone
        ? [detectedTimezone]
        : user?.timezone
          ? [user.timezone]
          : [],
    },
  });

  const handleSubmit = useCallback(() => {
    return form.handleSubmit(async (data) => {
      try {
        await mutation.mutateAsync(data);
        form.reset(data);
      } catch (error) {
        form.setError("root", {
          type: "manual",
          message:
            error instanceof Error ? error.message : "Failed to update timezone",
        });
      }
    })();
  }, [form, mutation]);

  const handleCancel = () => {
    form.reset();
  };

  const { isSubmitting, isValid, isDirty } = form.formState;

  // Sync form state with parent when action buttons are hidden
  // Allow proceeding when form is valid even if not dirty (e.g., timezone already saved)
  useStepFormSync({
    shouldSync: !showActionButtons,
    onStepStateChange,
    submitRef,
    formState: { isValid, isDirty, isSubmitting },
    onSubmit: handleSubmit,
    canProceed: isValid && !isSubmitting,
  });

  if (!user) {
    return <Loader />;
  }

  return (
    <div className={className}>
      <TimezoneForm
        form={form}
        timezoneOptions={timezoneOptions}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        primaryButtonText={primaryButtonText}
        showCancelButton={showCancelButton}
        showActionButtons={showActionButtons}
        size={size}
        label={label}
        layout={layout}
      />
    </div>
  );
}

export default TimezoneFormContainer;
